import { jsx as _jsx } from "react/jsx-runtime";
import { useAiLoader } from '@/components/ui/loader';
import { useConfigContext, useToast } from '@/hoc';
import { useGenerateJD, useJobDescription, useJobDescriptionByJobReference, } from '@/hoc/api/api-service';
import { clearFormStorage } from '@/utils';
import { parseScheduleSession } from '@/utils/vriti-session-payload';
import { isJdLocked } from '@/utils/interview-lock';
import { createContext, useContext, useEffect, useState } from 'react';
import { useWizardDraft } from './use-wizard-draft';
export const FormContext = createContext(undefined);
export const useFormContext = () => {
    const context = useContext(FormContext);
    if (!context) {
        throw new Error('useFormContext must be used within a FormProvider');
    }
    return context;
};
export const FormProvider = ({ children }) => {
    const [hasScheduledInterviews, setHasScheduledInterviews] = useState(false);
    const { show: showAiLoader, hide: hideAiLoader } = useAiLoader();
    const [jobDescriptionId, setJobDescriptionId] = useState('');
    const [jobReferenceId, setJobReferenceId] = useState('');
    const { config: { orgId, isNurture }, } = useConfigContext();
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        uploadedFile: null,
        manualJD: '',
        jobTitle: '',
        duration: 0,
        activeTab: 'upload',
        originalJD: '', // Initially empty
        savedJD: '',
        isDirty: false,
        criteriaDirty: false,
        timeCappingDirty: false,
        criteriaEdited: false,
        jobDescriptionId: '',
        interviewLevel: '',
        currentJD: "",
        interview_type: '',
        jdPrompt: '',
        generatedJD: "",
        isGenerating: false,
        criteria: [
            { id: '1', criteria: 'Technical Skills' },
            { id: '2', criteria: 'Behavioural Questions' },
            { id: '3', criteria: 'Culture Fit' },
            { id: '4', criteria: '' },
        ],
        toEmails: [],
        ccEmails: [],
        bccEmails: [],
        emailSubject: '',
        max_experience: 10,
        min_experience: 0,
        emailBody: `<p>Dear {candidate_name},</p>

<p>Thank you for interviewing for the role of <strong>{job_title}</strong>.</p>

<ul>
  <li><strong>Final Score:</strong> {final_score}</li>
  <li><strong>Status:</strong> {status}</li>
</ul>

<p>
  <strong>Strengths:</strong>
  <ul>
    {#strengths}
      <li>{strength}</li>
    {/strengths}
  </ul>
</p>

<p>
  <strong>Areas to Improve:</strong>
  <ul>
    {#improvements}
      <li>{improvement}</li>
    {/improvements}
  </ul>
</p>

<p>
<strong>Cheating Detection Result:</strong>
  <ul>
      <li>{cheating_detection_result}</li>
  </ul>
</p>

<p>{reason}</p>`,
        voiceConfig: {
            gender: 'male',
            locale: 'en-US',
            style: 'general',
            voice: 'en-IN-AaravNeural',
            openai_voice: 'cedar',
        },
        // Cost-optimized is the default mode for a new JD. The recruiter can switch
        // to Realtime in interview setup and that choice wins — this is only the
        // starting point, never a restriction.
        interview_taken_by: 'Azure',
    });
    // VA-29255: drafts are for the recruiter's own setup in this app. A job handed over
    // by the host (Vriti session payload, ?jd-id= job reference, nurture modal) is
    // filled by that flow, so it is left alone. `hydrated` turns true once the wizard
    // shows what the server has (immediately for a new job), so the draft baseline is
    // never taken from a half-loaded form.
    const [draftsApply] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return (!isNurture && !params.get('jd-id') && !sessionStorage.getItem('ai-interview-schedule-data'));
    });
    const [hydrated, setHydrated] = useState(() => !window.location.pathname.split('/')[2]);
    // Extract jobDescriptionId from the URL (/schedule-interview/:jd-id?search=...)
    useEffect(() => {
        const url = new URL(window.location.href);
        const pathSegments = url.pathname.split('/');
        const jobDescriptionIdFromUrl = pathSegments[2] || '';
        const searchParams = new URLSearchParams(url.search);
        const jdIdFromSearch = searchParams.get('jd-id');
        // Only set if not already persisted
        setFormData((prev) => (Object.assign(Object.assign({}, prev), { jobDescriptionId: prev.jobDescriptionId || jobDescriptionIdFromUrl, jobReferenceId: prev.jobReferenceId || jdIdFromSearch || undefined })));
        // Optionally also update state
        if (!jobDescriptionId)
            setJobDescriptionId(jobDescriptionIdFromUrl);
        if (jdIdFromSearch && !jobReferenceId)
            setJobReferenceId(jdIdFromSearch);
    }, []);
    const { data: jobDescription, isLoading: isLoadingJd } = useJobDescription(jobDescriptionId, orgId);
    const { data: jobDescriptionByReference, isLoading: isLoadingJobData } = useJobDescriptionByJobReference(jobReferenceId, orgId);
    const generateJDMutation = useGenerateJD();
    const { showToast } = useToast();
    const draft = useWizardDraft({
        formData,
        setFormData,
        enabled: draftsApply && hydrated && !isLoadingJd && !isLoadingJobData,
        locked: hasScheduledInterviews,
    });
    const updateFormData = (key, value) => {
        setFormData((prev) => (Object.assign(Object.assign({}, prev), { [key]: value })));
    };
    useEffect(() => {
        const aiInterviewScheduleData = sessionStorage.getItem('ai-interview-schedule-data');
        if (!aiInterviewScheduleData)
            return;
        // Vriti's payload is a single object, an array of objects (one per candidate)
        // or the legacy parallel-array shape — the shared parser hides the difference.
        const session = parseScheduleSession(aiInterviewScheduleData);
        if (!session)
            return;
        const parsedData = {
            jobReferenceId: session.job.jobReferenceId || undefined,
            jobDescription: session.job.jobDescription,
            candidateEmails: session.candidates.map((c) => c.email).filter(Boolean),
            recruiterEmails: session.job.recruiterEmails,
        };
        setJobReferenceId(session.job.jobReferenceId);
        // Only generate JD if no jobReferenceId already exists
        if (parsedData.jobDescription && !jobReferenceId) {
            showAiLoader("Fetching Job Description");
            generateJDMutation.mutate({
                prompt: parsedData.jobDescription,
                jobReferenceId: parsedData.jobReferenceId || undefined,
                orgId: orgId,
            }, {
                onSuccess: (data) => {
                    hideAiLoader();
                    setFormData((prev) => {
                        var _a, _b, _c, _d;
                        return (Object.assign(Object.assign({}, prev), { jobReferenceId: parsedData.jobReferenceId || prev.jobReferenceId || data.job_reference_id, jobDescriptionId: prev.jobDescriptionId || data.id, manualJD: prev.manualJD || data.description, originalJD: prev.manualJD || data.description, savedJD: data.description, activeTab: 'manual', min_experience: data.min_experience || 0, max_experience: data.max_experience || 0, interview_type: data.interview_type || '', processedJobDescription: data.description, jobTitle: prev.jobTitle || data.title || '', noOfCodingQuestions: (prev === null || prev === void 0 ? void 0 : prev.noOfCodingQuestions) || (data === null || data === void 0 ? void 0 : data.no_of_coding_questions) || 3, timeCapping: (prev === null || prev === void 0 ? void 0 : prev.timeCapping) || (data === null || data === void 0 ? void 0 : data.time_capping) || 10, criteria: ((_a = prev.criteria) === null || _a === void 0 ? void 0 : _a.length) ? prev.criteria : data.suggested_criteria || [], duration: prev.duration || data.interview_duration || 0, interviewLevel: prev.interviewLevel || data.interview_level || '', passing_score: prev.passing_score || (data === null || data === void 0 ? void 0 : data.passing_score) || 0, toEmails: ((_b = prev === null || prev === void 0 ? void 0 : prev.toEmails) === null || _b === void 0 ? void 0 : _b.length)
                                ? prev.toEmails
                                : parsedData.candidateEmails, ccEmails: ((_c = prev.ccEmails) === null || _c === void 0 ? void 0 : _c.length) ? prev.ccEmails : parsedData.recruiterEmails, emailSubject: prev.emailSubject || `Interview Report for ${data.title}`, emailBody: prev.emailBody || data.description, 
                            // Preserve any mode the recruiter already picked; don't reset it
                            // just because the JD text was (re)generated by AI.
                            interview_taken_by: (_d = prev.interview_taken_by) !== null && _d !== void 0 ? _d : 'Azure' }));
                    });
                },
                onError: (error) => {
                    hideAiLoader();
                    showToast({
                        message: 'Error generating job description',
                        description: error.message,
                        status: 'error',
                    });
                },
            });
        }
        else {
            // Only populate recruiter email if not already set
            setFormData((prev) => {
                var _a;
                return (Object.assign(Object.assign({}, prev), { ccEmails: ((_a = prev.ccEmails) === null || _a === void 0 ? void 0 : _a.length) ? prev.ccEmails : parsedData.recruiterEmails }));
            });
        }
        return () => {
            clearFormStorage();
        };
    }, []);
    // useEffect(() => {
    //   const aiInterviewScheduleData = sessionStorage.getItem('ai-interview-schedule-data');
    //   setFormData((prev) => ({
    //     ...prev,
    //    // manualJD: prev.activeTab === 'manual' || aiInterviewScheduleData ? prev.manualJD : '',
    //     originalJD: prev.activeTab === 'manual' || aiInterviewScheduleData ? prev.manualJD : '',
    //    // uploadedFile: prev.activeTab === 'upload' ? prev.uploadedFile : null,
    //   }));
    // }, [formData.activeTab]);
    useEffect(() => {
        const jobDesc = jobDescription || jobDescriptionByReference;
        if (jobDesc) {
            // A job with any interview linked is read-only: every wizard step shows the saved
            // data, nothing is saved or regenerated, and the recruiter can still schedule.
            setHasScheduledInterviews(isJdLocked(jobDesc.interview_stats));
            setHydrated(true);
            // What the server has is now on screen: that's the draft baseline.
            draft.markSaved();
            setFormData((prev) => {
                var _a, _b, _c, _d, _e, _f, _g, _h;
                return (Object.assign(Object.assign({}, prev), { jobReferenceId: jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.job_reference_id, jobDescriptionId: jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.id, manualJD: jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.description, savedJD: jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.description, activeTab: 'manual', min_experience: (jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.min_experience) || 0, max_experience: (jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.max_experience) || 0, timeCapping: (jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.time_capping) || 10, noOfCodingQuestions: (jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.no_of_coding_questions) || 3, processedJobDescription: jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.description, interview_type: (_b = (_a = jobDesc.interview_type) !== null && _a !== void 0 ? _a : prev.interview_type) !== null && _b !== void 0 ? _b : '', jobTitle: (jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.title) || '', ccEmails: (jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.recruiter_email) ? [jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.recruiter_email] : [], criteria: (jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.suggested_criteria) || [], duration: (jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.interview_duration) || 0, interviewLevel: (jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.interview_level) || '', passing_score: jobDesc.passing_score || 0, ask_dynamic_question: jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.ask_dynamic_question, should_ask_followup_question: jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.should_ask_followup_question, 
                    // Only an explicit Realtime choice yields Realtime. Tested this way round
                    // so a JD with no stored mode follows the current default (cost-optimized)
                    // rather than the one that happened to be default when it was created.
                    interview_taken_by: (jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.interview_taken_by) === 'OpenAI' ? 'OpenAI' : 'Azure', voice_provider_id: (jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.voice_provider_id) || undefined, voice_language: (jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.voice_language) || undefined, voice_id: (jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.voice_id) || undefined, voiceConfig: Object.assign(Object.assign({}, prev.voiceConfig), { gender: ((_c = jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.voice_config) === null || _c === void 0 ? void 0 : _c.gender) === 'female' || ((_d = jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.voice_config) === null || _d === void 0 ? void 0 : _d.gender) === 'male'
                            ? jobDesc.voice_config.gender
                            : prev.voiceConfig.gender, locale: ((_e = jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.voice_config) === null || _e === void 0 ? void 0 : _e.locale) || prev.voiceConfig.locale || 'en-US', voice: ((_f = jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.voice_config) === null || _f === void 0 ? void 0 : _f.voice) || prev.voiceConfig.voice, style: ((_g = jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.voice_config) === null || _g === void 0 ? void 0 : _g.style) || prev.voiceConfig.style, openai_voice: (((_h = jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.voice_config) === null || _h === void 0 ? void 0 : _h.openai_voice) && jobDesc.voice_config.openai_voice !== 'alloy')
                            ? jobDesc.voice_config.openai_voice
                            : 'cedar' }), emailSubject: `Interview Report for ${jobDesc === null || jobDesc === void 0 ? void 0 : jobDesc.title}`, emailBody: `<p>Dear {candidate_name},</p>

<p>Thank you for interviewing for the role of <strong>{job_title}</strong>.</p>

<ul>
  <li><strong>Final Score:</strong> {final_score}</li>
  <li><strong>Status:</strong> {status}</li>
</ul>

<p>
  <strong>Strengths:</strong>
  <ul>
    {#strengths}
      <li>{strength}</li>
    {/strengths}
  </ul>
</p>

<p>
  <strong>Areas to Improve:</strong>
  <ul>
    {#improvements}
      <li>{improvement}</li>
    {/improvements}
  </ul>
</p>

<p>
<strong>Cheating Detection Result:</strong>
  <ul>
      <li>{cheating_detection_result}</li>
  </ul>
</p>

<p>{reason}</p>
` }));
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobDescription, jobDescriptionByReference, setFormData, setStep]);
    return (_jsx(FormContext.Provider, { value: {
            step,
            setStep,
            formData,
            updateFormData,
            setFormData,
            // First load only: a background refetch (e.g. after a save) keeps the
            // wizard on screen instead of swapping it for its skeleton.
            isLoading: isLoadingJd || isLoadingJobData,
            jobDescription,
            hasScheduledInterviews,
            setHasScheduledInterviews,
            jobDescriptionId: formData === null || formData === void 0 ? void 0 : formData.jobDescriptionId,
            draft,
        }, children: children }));
};
